import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PrivacyTermsComponentSkeleton from './PrivacyTermsComponentSkeleton';
import ErrorState from '../ErrorState/ErrorState';
import { useCachedFetch } from './../../hooks/useCachedFetch';
import './PrivacyTermsComponent.scss';

const PrivacyTermsComponent = ({ type = 'privacy' }) => {
    const { i18n } = useTranslation();
    const currentLang = i18n.language;
    
    const { 
        data: legalData, 
        loading: dataLoading, 
        error: dataError, 
        refetch 
    } = useCachedFetch('/data/locales/legal.json');

    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        if (dataLoading) return;

        if (dataError) {
            setHasError(true);
            setIsLoading(false);
            return;
        }

        const data = legalData?.pages?.[type];
        
        if (data && data.list && data.list.length > 0) {
            setHasError(false);
        } else {
            setHasError(true);
        }

        setIsLoading(false);
    }, [dataLoading, dataError, legalData, type]);

    const handleRetry = useCallback(() => {
        refetch();
    }, [refetch]);

    const privacyTermsData = useMemo(() => 
        legalData?.pages?.[type] || null,
        [legalData, type]
    );

    const getLocalizedText = useCallback((text) => {
        if (!text) return '';
        if (typeof text === 'object') {
            return text[currentLang] || text.en || '';
        }
        return text;
    }, [currentLang]);

    const localizedSections = useMemo(() => {
        if (!privacyTermsData?.list) return [];

        return privacyTermsData.list.map((section, index) => ({
            ...section,
            localizedTitle: getLocalizedText(section.title),
            localizedContent: section.content.map(block => ({
                ...block,
                localizedValue: getLocalizedText(block.value),
                localizedItems: Array.isArray(getLocalizedText(block.items)) 
                    ? getLocalizedText(block.items) 
                    : []
            }))
        }));
    }, [privacyTermsData, getLocalizedText]);

    const renderContentBlock = useCallback((block, index) => {
        switch (block.type) {
            case 'text':
                return (
                    <p className='privacy-terms__rules__item__content' key={index}>
                        {block.localizedValue}
                    </p>
                );
            case 'subtitle':
                return (
                    <h4 className='privacy-terms__rules__item__subtitle' key={index}>
                        {block.localizedValue}
                    </h4>
                );
            case 'list':
                return (
                    <ul className='privacy-terms__rules__item__list' key={index}>
                        {block.localizedItems.map((item, j) => (
                            <li className='privacy-terms__rules__item__list__item' key={j}>
                                {item}
                            </li>
                        ))}
                    </ul>
                );
            default:
                return null;
        }
    }, []);

    const hasValidData = useMemo(() => 
        !hasError && privacyTermsData && localizedSections.length > 0,
        [hasError, privacyTermsData, localizedSections.length]
    );

    if (isLoading) {
        return <PrivacyTermsComponentSkeleton />;
    }

    if (!hasValidData) {
        return <ErrorState onRetry={handleRetry} />;
    }

    return (
        <div className="privacy-terms__wrapper">
            {localizedSections.map((section, index) => (
                <div className='privacy-terms__rules__item' key={index}>
                    <div className='privacy-terms__rules__item__title__wrapper'>
                        <h3 className="privacy-terms__rules__item__title">
                            {section.localizedTitle}
                        </h3>
                    </div>
                    <div className='privacy-terms__rules__item__content__wrapper'>
                        {section.localizedContent.map((block, i) => 
                            renderContentBlock(block, i)
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default React.memo(PrivacyTermsComponent);